import { formatEuros, formatPercent, formatQuantity } from "../../../dashboard-framework/format";
import { buildPivot, PivotMeasure, PivotNode } from "../../../dashboard-framework/pivot";
import { ATTENDED_SESSION_STATUSES, FAILED_SESSION_STATUSES, SEGMENT_ROW_ORDER } from "./digitalMarketingConfig";
import { isQualifiedLeadStatus, leadTypeFromClientType } from "./digitalMarketingRows";

// Comparação crianças vs adultos: TUDO parte de um único universo de leads (pela data de criação da lead),
// com as suas sessões de 1ª visita e os tratamentos do cliente. Assim as métricas ao nível da LEAD e ao
// nível da SESSÃO reconciliam por construção — e cada célula do pivot pode abrir as mesmas leads que contou.

export type SegmentLeadSession = { service: number | string | null; status: string | null };
export type SegmentLeadTreatment = { date_of_start: string | null };

// first_name/last_name/date_created só alimentam as colunas do drill; status alimenta também a qualificação
// (denominador do custo/lead), por isso tem de vir sempre na query.
export type SegmentLead = {
    id: number | string;
    client_type: string | null;
    sessions: Array<SegmentLeadSession> | null;
    client: { treatments: Array<SegmentLeadTreatment> | null } | null;
    first_name?: string | null;
    last_name?: string | null;
    date_created?: string | null;
    status?: string | null;
};

export type SegmentLeadRow = {
    leadId: number | string;
    leadName: string;
    dateCreated: string | null;
    status: string | null;
    segment: string;
    qualified: boolean;
    booked: boolean;
    resolvedSessions: number;
    attendedSessions: number;
    attended: boolean;
    bought: boolean;
};

export type SegmentStage = { label: string; columnKey: string };

const leadDisplayName = (lead: SegmentLead) => {
    const name = [lead.first_name, lead.last_name].filter(Boolean).join(" ").trim();
    const displayName = name || `Lead #${lead.id}`;
    return displayName;
};

// Uma linha por lead com todos os predicados do funil já resolvidos.
// "Marcou" = criou alguma sessão de 1ª visita (qualquer estado, alguma vez) — histórico, sobrevive a
// cancelamentos (ao contrário de leads.progress, que reverte). "Compareceu" = alguma sessão realizada,
// independentemente de quantas vezes remarcou. "Comprou" = tratamento com data de início real.
export const segmentLeadRows = (leads: Array<SegmentLead>, firstVisitServices: Array<number>) => {
    const rows = leads.map((lead) => {
        const firstVisitSessions = (lead.sessions || []).filter((session) => firstVisitServices.includes(Number(session.service)));
        const attendedSessions = firstVisitSessions.filter((session) => ATTENDED_SESSION_STATUSES.includes(session.status || "")).length;
        const failedSessions = firstVisitSessions.filter((session) => FAILED_SESSION_STATUSES.includes(session.status || "")).length;
        const row: SegmentLeadRow = {
            leadId: lead.id,
            leadName: leadDisplayName(lead),
            dateCreated: lead.date_created ?? null,
            status: lead.status ?? null,
            segment: leadTypeFromClientType(lead.client_type),
            qualified: isQualifiedLeadStatus(lead.status),
            booked: firstVisitSessions.length > 0,
            resolvedSessions: attendedSessions + failedSessions,
            attendedSessions,
            attended: attendedSessions > 0,
            bought: (lead.client?.treatments || []).some((treatment) => Boolean(treatment.date_of_start)),
        };
        return row;
    });
    return rows;
};

const countWhere = (rows: Array<SegmentLeadRow>, predicate: (row: SegmentLeadRow) => boolean) => rows.filter(predicate).length;
const sumOf = (rows: Array<SegmentLeadRow>, valueOf: (row: SegmentLeadRow) => number) => rows.reduce((total, row) => total + valueOf(row), 0);
const percentOrNull = (numerator: number, denominator: number) => (denominator > 0 ? (numerator / denominator) * 100 : null);
const costPerUnitOrNull = (cost: number, units: number) => (cost > 0 && units > 0 ? cost / units : null);

// Custo publicitário BLENDED repartido pró-rata pela quota de leads do segmento: não há atribuição de
// gasto por segmento (a maioria das campanhas não distingue criança/adulto), por isso cada segmento paga
// a fatia do gasto que corresponde à sua fatia de leads — TODAS as leads, lixo incluído. O custo/lead
// divide essa fatia só pelas leads QUALIFICADAS, logo um segmento que gere mais lixo paga mais por lead
// qualificada: ao contrário das restantes métricas de custo, o custo/lead já não é igual entre segmentos.
const allocatedCost = (segmentRows: Array<SegmentLeadRow>, allRows: Array<SegmentLeadRow>, totalSpend: number) => {
    const cost = allRows.length > 0 ? (totalSpend * segmentRows.length) / allRows.length : 0;
    return cost;
};

const quantityCell = (value: number | null) => formatQuantity(value || 0);
const euroCell = (value: number | null) => (value === null ? "" : formatEuros(value));
const percentCell = (value: number | null) => (value === null ? "" : formatPercent(value));

export const segmentFunnelMeasures = (totalSpend: number): Array<PivotMeasure<SegmentLeadRow>> => {
    const measures: Array<PivotMeasure<SegmentLeadRow>> = [
        { key: "leads", label: "Leads", isNumeric: true, compute: (rows) => rows.length, format: quantityCell },
        { key: "qualifiedLeads", label: "Leads qualif.", isNumeric: true, compute: (rows) => countWhere(rows, (row) => row.qualified), format: quantityCell },
        { key: "cost", label: "Custo alocado", isNumeric: true, compute: (rows, allRows) => allocatedCost(rows, allRows, totalSpend), format: euroCell },
        {
            key: "costPerLead",
            label: "Custo/lead qualificada",
            isNumeric: true,
            compute: (rows, allRows) => costPerUnitOrNull(allocatedCost(rows, allRows, totalSpend), countWhere(rows, (row) => row.qualified)),
            format: euroCell,
        },
        { key: "booked", label: "Marcaram", isNumeric: true, compute: (rows) => countWhere(rows, (row) => row.booked), format: quantityCell },
        {
            key: "pctBooking",
            label: "% marcação",
            isNumeric: true,
            compute: (rows) => percentOrNull(countWhere(rows, (row) => row.booked), rows.length),
            format: percentCell,
        },
        {
            key: "resolvedSessions",
            label: "Sessões resolvidas",
            isNumeric: true,
            compute: (rows) => sumOf(rows, (row) => row.resolvedSessions),
            format: quantityCell,
        },
        {
            key: "attendedSessions",
            label: "Sessões feitas",
            isNumeric: true,
            compute: (rows) => sumOf(rows, (row) => row.attendedSessions),
            format: quantityCell,
        },
        {
            key: "pctAttendance",
            label: "% comparência (sessão)",
            isNumeric: true,
            compute: (rows) => percentOrNull(sumOf(rows, (row) => row.attendedSessions), sumOf(rows, (row) => row.resolvedSessions)),
            format: percentCell,
        },
        { key: "attendedLeads", label: "Compareceram", isNumeric: true, compute: (rows) => countWhere(rows, (row) => row.attended), format: quantityCell },
        {
            key: "pctLeadToEval",
            label: "% lead→avaliação",
            isNumeric: true,
            compute: (rows) => percentOrNull(countWhere(rows, (row) => row.attended), rows.length),
            format: percentCell,
        },
        {
            key: "costPerEval",
            label: "Custo/avaliação",
            isNumeric: true,
            compute: (rows, allRows) => costPerUnitOrNull(allocatedCost(rows, allRows, totalSpend), countWhere(rows, (row) => row.attended)),
            format: euroCell,
        },
        { key: "bought", label: "Compraram", isNumeric: true, compute: (rows) => countWhere(rows, (row) => row.bought), format: quantityCell },
        {
            key: "pctEvalToBuy",
            label: "% avaliação→compra",
            isNumeric: true,
            compute: (rows) => percentOrNull(countWhere(rows, (row) => row.bought), countWhere(rows, (row) => row.attended)),
            format: percentCell,
        },
        {
            key: "pctLeadToBuy",
            label: "% lead→compra",
            isNumeric: true,
            compute: (rows) => percentOrNull(countWhere(rows, (row) => row.bought), rows.length),
            format: percentCell,
        },
        {
            key: "costPerSale",
            label: "Custo/venda",
            isNumeric: true,
            compute: (rows, allRows) => costPerUnitOrNull(allocatedCost(rows, allRows, totalSpend), countWhere(rows, (row) => row.bought)),
            format: euroCell,
        },
    ];
    return measures;
};

export const segmentFunnelPivot = (leadRows: Array<SegmentLeadRow>, totalSpend: number) => {
    const pivot = buildPivot<SegmentLeadRow>({
        items: leadRows,
        rowLevels: [(row) => row.segment],
        measures: segmentFunnelMeasures(totalSpend),
        sortRowsBy: [(first, second) => SEGMENT_ROW_ORDER.indexOf(first.label) - SEGMENT_ROW_ORDER.indexOf(second.label)],
    });
    return pivot;
};

// Etapas dos gráficos comparativos: cada etapa é uma categoria (barra agrupada) e cada segmento uma série.
export const SEGMENT_CONVERSION_STAGES: Array<SegmentStage> = [
    { label: "% marcação", columnKey: "pctBooking" },
    { label: "% comparência (sessão)", columnKey: "pctAttendance" },
    { label: "% lead→avaliação", columnKey: "pctLeadToEval" },
    { label: "% avaliação→compra", columnKey: "pctEvalToBuy" },
    { label: "% lead→compra", columnKey: "pctLeadToBuy" },
];

export const SEGMENT_COST_STAGES: Array<SegmentStage> = [
    { label: "Custo/lead qualificada", columnKey: "costPerLead" },
    { label: "Custo/avaliação", columnKey: "costPerEval" },
    { label: "Custo/venda", columnKey: "costPerSale" },
];

// Predicado de drill por coluna: cada célula abre exatamente as leads que a originaram. As colunas de
// custo e de rácio herdam o predicado do seu DENOMINADOR ou NUMERADOR (ex.: Custo/venda e % lead→compra
// abrem as leads que compraram, Custo/lead qualificada abre as leads qualificadas); Leads e Custo alocado
// não têm predicado — abrem todas as leads do segmento.
// ATENÇÃO: nas colunas de SEGMENT_SESSION_LEVEL_COLUMNS o valor da célula é uma soma de SESSÕES mas as
// linhas continuam a ser LEADS, logo o nº de registos da modal é MENOR do que a célula (uma lead pode ter
// várias sessões). Quem constrói o drill tem de mostrar a soma das sessões nos chips do cabeçalho.
const SEGMENT_DRILL_PREDICATE_BY_COLUMN: Record<string, (row: SegmentLeadRow) => boolean> = {
    qualifiedLeads: (row) => row.qualified,
    costPerLead: (row) => row.qualified,
    booked: (row) => row.booked,
    pctBooking: (row) => row.booked,
    resolvedSessions: (row) => row.resolvedSessions > 0,
    attendedSessions: (row) => row.attendedSessions > 0,
    pctAttendance: (row) => row.resolvedSessions > 0,
    attendedLeads: (row) => row.attended,
    pctLeadToEval: (row) => row.attended,
    costPerEval: (row) => row.attended,
    bought: (row) => row.bought,
    pctEvalToBuy: (row) => row.bought,
    pctLeadToBuy: (row) => row.bought,
    costPerSale: (row) => row.bought,
};

// Colunas cujo valor é medido em SESSÕES (e não em leads) — ver aviso acima.
export const SEGMENT_SESSION_LEVEL_COLUMNS = ["resolvedSessions", "attendedSessions", "pctAttendance"];

export const segmentDrillRows = (segmentRows: Array<SegmentLeadRow>, columnKey: string) => {
    const predicate = SEGMENT_DRILL_PREDICATE_BY_COLUMN[columnKey];
    const rows = predicate ? segmentRows.filter(predicate) : segmentRows;
    return rows;
};

// Um ponto por etapa, com uma chave por segmento — células nulas ficam null (barra ausente, não zero).
export const segmentChartPoints = (rows: Array<PivotNode<SegmentLeadRow>>, stages: Array<SegmentStage>) => {
    const points = stages.map((stage) => {
        const point: Record<string, string | number | null> = { stage: stage.label };
        rows.forEach((row) => {
            point[row.label] = row.rawByColumn[stage.columnKey] ?? null;
        });
        return point;
    });
    return points;
};
